/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  TreeSelect,
  Typography,
} from 'antd';
import { getTipoDeEmpleado } from 'api/catalogos';
import { getAreasExternas } from 'api/catalogos/AreaExterna';
import { getCategoriaDePuesto } from 'api/catalogos/categoriaDePuestos';
import { getEstadosPlazas } from 'api/catalogos/estadosPlazas';
import { getMotivosDeMovimientosDeEmpleados } from 'api/catalogos/motivoDeMovimientoDeEmpleados';
import { getNombramiento } from 'api/catalogos/Nombramientos';
import { getPlazavistaOptimized } from 'api/catalogos/plazas';
import { getPuesto, getPuestos } from 'api/catalogos/puestos';
import { getDatoOperacionalesDeEmpleados } from 'api/estructuraOrganizacional/datosOperacionalesDeEmpleados';

import currencyFormatter from 'utils/currencyFormatter';
import Select from 'components/Select';
import moment from 'moment/moment';
import React, {
  useEffect, useMemo, useState,
} from 'react';
import { ESTADO_PLAZA_ASIGNADA, ESTATUS_AUTORIZADO } from 'utils/constants';

// El endpoint de áreas regresa un listado plano (id, clave, nombre, directorio_area_padre);
// TreeSelect necesita nodos {title, value, key, children} para poder mostrar las etiquetas.
const buildAreasTreeData = (areasData = []) => {
  const nodeById = new Map(areasData.map((item) => [item.id, {
    title: `${item.clave} - ${(item.nombre || '').trim()}`,
    value: item.id,
    key: item.id,
    children: [],
  }]));

  const tree = [];
  areasData.forEach((item) => {
    const node = nodeById.get(item.id);
    const parentNode = nodeById.get(item.directorio_area_padre);
    if (parentNode) {
      parentNode.children.push(node);
    } else {
      tree.push(node);
    }
  });

  return tree;
};

const EmpleadoAdscripcion = ({
  form,
  rules,
  puestos,
  formPlazas,
  areas,
  categoriaDePuestos,
  tiposEmpleados,
  tiposDeFunciones,
  tabuladoresGenerales,
  calcularAntiguedad,
  setFechaDeAlta,
  setFechaDeBaja,
  setFechaDeReingreso,
  isBlocked,
  empleado,
  nombramientosSinPaginar,
  plazaEmpleado,
}) => {
  const [tabuladorGeneral, setTabuladorGeneral] = useState();
  const [tipoEmpleado, setTipoEmpleado] = useState();
  const [categoria, setCategoria] = useState();
  const [motivoAltaRequerido, setMotivoAltaRequerido] = useState(false);
  const [motivoBajaRequerido, setMotivoBajaRequerido] = useState(false);
  const [motivosMovimientos, setMotivosMovimientos] = useState([]);
  const [, setTipoFuncionId] = useState(null);
  const [cuipHabilitadoLocal, setCuipHabilitadoLocal] = useState(false);
  const [estadosPlaza, setEstadosPlaza] = useState([]);
  const [motivosMovimiento, setMotivosMovimiento] = useState([]);
  const [areasExternas, setAreasExternas] = useState([]);
  const [isComisionadoExterno, setIsComisionadoExterno] = useState(false);
  const [isComisionadoInterno, setIsComisionadoInterno] = useState(false);

  const areasTreeData = useMemo(() => buildAreasTreeData(areas), [areas]);

  const evaluarFuncion = (valor) => {
    const funcionSeleccionada = tiposDeFunciones.find((f) => f.id === valor);
    const esSeguridadPublica = funcionSeleccionada?.descripcion === 'Seguridad Pública';
    setCuipHabilitadoLocal(esSeguridadPublica);
    if (!esSeguridadPublica) {
      form.setFieldsValue({ cuip: null });
    }
  };

  const changeFechaAlta = async (value) => {
    const _motivosMovimientosEmpleados = await getMotivosDeMovimientosDeEmpleados();
    setMotivosMovimientos(_motivosMovimientosEmpleados);
    setFechaDeAlta(value);
    if (value) {
      setMotivoAltaRequerido(true);
      form.setFieldsValue({
        antiguedad: calcularAntiguedad(value),
      });
    } else {
      setMotivoAltaRequerido(false);
      form.setFieldsValue({
        antiguedad: null,
        motivo_alta: null,
      });
    }
  };

  const changeFechaBaja = async (value) => {
    const _motivosMovimientosEmpleados = await getMotivosDeMovimientosDeEmpleados();
    setMotivosMovimientos(_motivosMovimientosEmpleados);
    setFechaDeBaja(value);
    if (value) {
      setMotivoBajaRequerido(true);
    } else {
      setMotivoBajaRequerido(false);
      form.setFieldsValue({ motivo_baja: null });
    }
  };

  useEffect(() => {
    const toMoment = (val) => (val ? moment(val) : null);

    const fetch = async () => {
      try {
        if (!form || !formPlazas || !empleado) return;

        // Carga de catálogos concurrentes
        const [
          _estadosPlaza,
          motivos,
          _areasExternas,
        ] = await Promise.all([
          getEstadosPlazas(),
          getMotivosDeMovimientosDeEmpleados(),
          getAreasExternas(),
        ]);

        setMotivosMovimiento(motivos);
        setEstadosPlaza(_estadosPlaza);
        setAreasExternas(_areasExternas);

        const datosOp = await getDatoOperacionalesDeEmpleados({ empleado });
        if (!datosOp?.length) return;

        const dato = datosOp[0];

        // Sueldos
        const sueldoMensual = parseFloat(dato.sueldo_mensual) || null;
        const sueldoDiario = sueldoMensual ? (sueldoMensual / 30).toFixed(2) : null;

        formPlazas.setFieldsValue({
          sueldo_mensual: sueldoMensual,
          sueldo_diario: sueldoDiario,
        });

        let nombramiento = null;
        if (dato.nombramiento) {
          nombramiento = await getNombramiento(dato.nombramiento);
        }

        // Fechas y motivos
        const fechaAltaGob = dato.fecha_de_alta_en_gobierno || nombramiento?.fecha_de_alta_en_gobierno;
        const fechaAlta = dato.fecha_de_alta || nombramiento?.fecha_de_alta;
        const fechaBaja = dato.fecha_de_baja || nombramiento?.fecha_de_baja;
        const fechaReingreso = dato.fecha_de_reingreso || nombramiento?.fecha_de_reingreso;

        const motivoAltaId = dato.motivo_alta ?? nombramiento?.motivo_alta ?? null;
        const motivoBajaId = dato.motivo_baja ?? nombramiento?.motivo_baja ?? null;

        form.setFieldsValue({
          fecha_de_alta_en_gobierno: toMoment(fechaAltaGob),
          fecha_de_alta: toMoment(fechaAlta),
          fecha_de_baja: toMoment(fechaBaja),
          fecha_de_reingreso: toMoment(fechaReingreso),
          motivo_alta: motivoAltaId,
          motivo_baja: motivoBajaId,
          antiguedad: fechaAlta ? calcularAntiguedad(toMoment(fechaAlta)) : null,
        });

        if (fechaAlta) setMotivoAltaRequerido(true);
        if (fechaBaja) setMotivoBajaRequerido(true);

        // Carga de plaza
        if (dato.plaza) {
          const plazaResponse = await getPlazavistaOptimized(dato.plaza);
          const plaza = Array.isArray(plazaResponse) ? plazaResponse[0] : plazaResponse;

          if (plaza) {
            setTimeout(() => {
              // comisionado_en_area_externa / area_externa_de_comision son campos de
              // Empleado (no de DatoOperacionalDelEmpleado, de donde viene `dato`) -- el
              // padre (Empleados.jsx) ya los deja cargados en formPlazas desde el
              // Empleado real antes de que este efecto corra. Aqui solo se refleja ese
              // valor ya correcto en el estado local; NO se vuelve a leer de `dato`
              // (siempre venia undefined/false y pisaba lo que el usuario ya guardo).
              setIsComisionadoExterno(formPlazas.getFieldValue('comisionado_en_area_externa') || false);
              setIsComisionadoInterno(formPlazas.getFieldValue('comisionado_en_area_interna') || false);

              formPlazas.setFieldsValue({
                clave: plaza.clave_y_descripcion?.trim() || plaza.clave || null,
                directorio_area: plaza.directorio_area_clave_y_nombre?.trim() || plaza.directorio_area?.trim() || null,
                area: plaza.directorio_de_area_nombre?.trim() || plaza.directorio_area?.trim() || null,
                tipo_de_empleado: plaza.tipo_de_empleado?.trim() || null,
                categoria_de_empleado: plaza.categoria_de_empleado?.trim() || null,
                coordinacion: plaza.coordinacion?.trim() || plaza.coordinacion_nombre?.trim() || null,

                directorio_de_area_comision: dato.directorio_de_area_comision || null,
              });

              setTimeout(() => {
                const puestoFinal = plaza.puesto_clave_y_descripcion?.trim() || plaza.puesto?.trim() || '';
                formPlazas.setFieldsValue({ puesto: puestoFinal });
              }, 150);
            }, 0);
          }
        }

        // Tipo de función
        if (dato.tipo_de_funcion) {
          form.setFieldsValue({ tipo_de_funcion: dato.tipo_de_funcion });
          evaluarFuncion(dato.tipo_de_funcion);
        }

        // Motivos de movimientos
        const _motivosMovimientosEmpleados = await getMotivosDeMovimientosDeEmpleados();
        setMotivosMovimientos(_motivosMovimientosEmpleados);

        if (motivoAltaId && !_motivosMovimientosEmpleados.some((m) => m.id === motivoAltaId)) {
          form.setFieldsValue({ motivo_alta: 'N/A' });
        }

        if (motivoBajaId && !_motivosMovimientosEmpleados.some((m) => m.id === motivoBajaId)) {
          form.setFieldsValue({ motivo_baja: 'N/A' });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[Adscripción del Empleado] error al cargar datos operacionales/plaza/nombramiento', err);
      }
    };

    fetch();
  }, [empleado, form, formPlazas, puestos, areas, tiposEmpleados, categoriaDePuestos]);

  useEffect(() => {
    const tipoFuncion = form.getFieldValue('tipo_de_funcion');

    if (
      tipoFuncion !== undefined
      && tipoFuncion !== null
      && tiposDeFunciones.length > 0
    ) {
      evaluarFuncion(tipoFuncion);
    }
  }, [tiposDeFunciones, form]);

  return (
    <>
      <Card
        size="small"
        className="inner-block-card"
      >
        <Typography.Text strong style={{ fontSize: 16 }}>
          {[
            form.getFieldValue('nombre'),
            form.getFieldValue('apellido_paterno'),
            form.getFieldValue('apellido_materno'),
          ].filter(Boolean).join(' ')}
        </Typography.Text>
      </Card>
      <Card
        size="small"
        className="inner-block-card"
      >
        <Row gutter={10}>
          <Col xs={24} sm={24} md={8}>
            <Typography.Text type="secondary" style={{ display: 'block' }}>
              Sueldo Mensual
            </Typography.Text>
            <Typography.Text strong style={{ fontSize: 16 }}>
              {currencyFormatter(formPlazas.getFieldValue('sueldo_mensual') || 0)}
            </Typography.Text>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Typography.Text type="secondary" style={{ display: 'block' }}>
              Sueldo Diario
            </Typography.Text>
            <Typography.Text strong style={{ fontSize: 16 }}>
              {currencyFormatter(formPlazas.getFieldValue('sueldo_diario') || 0)}
            </Typography.Text>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Typography.Text type="secondary" style={{ display: 'block' }}>
              Antigüedad
            </Typography.Text>
            <Typography.Text strong style={{ fontSize: 16 }}>
              {form.getFieldValue('antiguedad') || ''}
            </Typography.Text>
          </Col>
        </Row>
      </Card>
      <Form layout="vertical" form={form} scrollToFirstError name="general">
        <Card
          type="inner"
          size="small"
          className="inner-block-card"
          title="1. Movimientos De Alta, Baja Y Reingreso"
          extra="Historial De Ingreso Y Salida"
        >
          <Row gutter={10}>
            <Col xs={24} sm={24} md={8}>
              <Form.Item name="fecha_de_alta_en_gobierno" label="Fecha de Alta en Gobierno" hasFeedback>
                <DatePicker placeholder="" format="DD/MM/YYYY" disabled={isBlocked} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item name="fecha_de_alta" label="Fecha de Alta" hasFeedback>
                <DatePicker
                  placeholder=""
                  format="DD/MM/YYYY"
                  onChange={(value) => changeFechaAlta(value)}
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item name="motivo_alta" label="Motivo de Alta" hasFeedback>
                <Select
                  disabled={!motivoAltaRequerido || isBlocked}
                  dataSource={motivosMovimientos.filter((e) => e.tipos_de_movimientos_de_empleados === 1)}
                  render={(value) => `${value?.concepto}`}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item name="fecha_de_baja" label="Fecha de Baja" hasFeedback>
                <DatePicker
                  placeholder=""
                  format="DD/MM/YYYY"
                  onChange={(value) => changeFechaBaja(value)}
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item name="motivo_baja" label="Motivo de Baja" rules={motivoBajaRequerido ? rules.required : null} hasFeedback>
                <Select disabled={!motivoBajaRequerido || isBlocked} dataSource={motivosMovimientos} render={(value) => `${value?.concepto}`} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item name="fecha_de_reingreso" label="Fecha de Reingreso" rules={rules.fecha_de_reingreso} hasFeedback>
                <DatePicker
                  placeholder=""
                  format="DD/MM/YYYY"
                  onChange={(value) => {
                    setFechaDeBaja();
                    setFechaDeReingreso(value);
                    setMotivoBajaRequerido(false);
                    form.resetFields(['fecha_de_baja', 'motivo_baja']);
                  }}
                  disabled={isBlocked}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        <Form.Item name="antiguedad" hidden>
          <Input disabled />
        </Form.Item>
        <Row gutter={10}>
          {/* }
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="solicita_no_presentar_calculo_anual_isr"
              label="Presenta Constancia para no Cálculo Anual"
              rules={rules.required}
              hasFeedback
            >
              <Select
                trueFalse
                disabled={isBlocked}
              />
            </Form.Item>
          </Col>
          */}
          {/*
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="fecha_de_aviso_no_calculo_anual_isr"
              label="Fecha de Aviso para Cálculo Anual de ISR"
              hasFeedback
            >
              <DatePicker
                placeholder=""
                format="DD/MM/YYYY"
                disabled={isBlocked}
              />
            </Form.Item>
          </Col>
          */}
        </Row>
      </Form>

      <Card
        type="inner"
        size="small"
        className="inner-block-card"
        title="2. Plaza, Puesto Y Área"
        extra="Adscripción Organizacional Actual"
      >
        <Form layout="vertical" form={formPlazas} scrollToFirstError name="plazas">
          <Row gutter={10}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item name="clave" label="No. de Plaza" hasFeedback>
                <Input allowClear disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item name="puesto" label="Puesto" hasFeedback>
                <Input allowClear disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item name="directorio_area" label="Área" hasFeedback>
                <Input allowClear disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Form.Item name="coordinacion" label="Coordinación" hasFeedback>
                <Input allowClear disabled />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="sueldo_mensual" hidden>
            <Input disabled />
          </Form.Item>
          <Form.Item name="sueldo_diario" hidden>
            <Input disabled />
          </Form.Item>
        </Form>
        <Form layout="vertical" form={form} scrollToFirstError name="general-funcion">
          <Row gutter={10}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item name="tipo_de_funcion" label="Tipo de Función" hasFeedback>
                <Select
                  keyProp="id"
                  labelProp="descripcion"
                  dataSource={tiposDeFunciones}
                  disabled
                  onChange={(value) => {
                    setTipoFuncionId(value);
                    evaluarFuncion(value);
                  }}
                />
              </Form.Item>
            </Col>
            {cuipHabilitadoLocal && (
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="cuip"
                  label="CUIP Clave Única de Identificación Permanente"
                  rules={[{ required: true, message: 'CUIP es requerido cuando la función es Seguridad Pública' }]}
                  hasFeedback
                >
                  <Input disabled={isBlocked} />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Card>

      <Card
        type="inner"
        size="small"
        className="inner-block-card"
        title="3. Comisión En Área Externa"
        extra="Aplica Solo Si El Empleado Está Comisionado"
      >
        <Form layout="vertical" form={formPlazas} scrollToFirstError name="plazas-comision">
          <Row gutter={10}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="comisionado_en_area_externa"
                label="Comisionado en Área Externa"
                hasFeedback
              >
                <Select
                  trueFalse // Componente Select modificado que renderiza SI / NO
                  disabled={isBlocked}
                  onChange={(value) => {
                    setIsComisionadoExterno(value);
                    // Limpiar el campo opuesto para no tener data basura al guardar
                    if (value) {
                      formPlazas.setFieldsValue({ directorio_de_area_comision: null });
                    } else {
                      formPlazas.setFieldsValue({ area_externa_de_comision: null });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            {isComisionadoExterno && (
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="area_externa_de_comision"
                  label="Área Externa de Comisión"
                  hasFeedback
                >
                  <Select
                    dataSource={areasExternas}
                    keyProp="id"
                    labelProp="nombre_del_area" // Ajustado según tu nuevo modelo AreaExterna
                    disabled={isBlocked}
                    allowClear
                  />
                </Form.Item>
              </Col>
            )}
            {/* Campo "Área de Comisión" (directorio_de_area_comision) oculto a petición:
                si comisionado_en_area_externa está en NO no debe mostrarse ningún campo.
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="directorio_de_area_comision"
                  label="Área de Comisión"
                  hasFeedback
                >
                  <TreeSelect
                    showSearch
                    treeNodeFilterProp="title"
                    treeData={areasTreeData}
                    disabled={isBlocked}
                    allowClear
                  />
                </Form.Item>
              </Col>
            */}
            {/* Si solo el interno está en SI, se deja vacía la segunda columna para que
                "Comisionado en Área Interna" baje a la siguiente fila junto a su área. */}
            {isComisionadoInterno && !isComisionadoExterno && (
              <Col xs={0} sm={0} md={12} />
            )}
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="comisionado_en_area_interna"
                label="Comisionado en Área Interna"
                hasFeedback
              >
                <Select
                  trueFalse
                  disabled={isBlocked}
                  onChange={(value) => {
                    setIsComisionadoInterno(value);
                    if (!value) {
                      formPlazas.setFieldsValue({ directorio_de_area_comisionado_area_interna: null });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            {isComisionadoInterno && (
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="directorio_de_area_comisionado_area_interna"
                  label="Área Interna de Comisión"
                  hasFeedback
                >
                  <TreeSelect
                    showSearch
                    treeNodeFilterProp="title"
                    treeData={areasTreeData}
                    disabled={isBlocked}
                    allowClear
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Card>

      <Card
        type="inner"
        size="small"
        className="inner-block-card"
        title="4. Estatus De Asignación De Plaza"
        extra="Movimiento Más Reciente"
      >
        <Form layout="vertical" form={form} scrollToFirstError name="datos nuevos datoOp">
          <Row gutter={10}>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="estado_plaza"
                label="Estatus de la Plaza"
                hasFeedback
                initialValue={ESTADO_PLAZA_ASIGNADA}
              >
                <Select dataSource={estadosPlaza} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="fecha_asignacion_desasignacion"
                label="Fecha de Asignación o Desasignación"
                hasFeedback
              >
                <DatePicker
                  allowClear
                  format="DD/MM/YYYY"
                  placeholder=""
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="motivo_asignacion_desasignacion"
                label="Motivo de Asignación o Desasignación"
                hasFeedback
              >
                <Select
                  dataSource={
                    // eslint-disable-next-line max-len
                    motivosMovimiento
                  }
                  render={(value) => `${value?.concepto}`}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </>
  );
};

export default EmpleadoAdscripcion;
